<template>
  <!-- 已跟客户确认(고객확인 완료) — 待客户确认 에서 처리(确认出库)된 건 + 处理状态=处理完成 으로 저장한 건이 여기로 온다. 入库处理 화면의 탭(2026-10-04 YZY) -->
  <div>
    <div class="d-flex flex-wrap align-items-center mb-2" style="gap:8px">
      <b-form-checkbox v-model="mineOnly" switch>
        只看我处理的<small>(내가 처리한 건만)</small> <small class="text-muted" v-if="me">— {{ me }}</small>
      </b-form-checkbox>
      <b-button size="sm" variant="outline-primary" @click="load" :disabled="loading">刷新<small>(새로고침)</small></b-button>
      <span class="small text-muted">共 {{ shownRows.length }}件</span>
    </div>

    <div v-if="loading" class="text-center my-4"><b-spinner variant="primary"></b-spinner></div>
    <div v-else class="table-responsive confirmed-list">
      <table class="table table-sm table-bordered table-hover mb-0">
        <thead class="thead-light">
          <!-- 열 순서는 YZY 지정(2026-10-04). 韩文·实际追加作业 은 뺐고, 목록 밖 确认照片 은 맨 뒤에 둔다 -->
          <tr>
            <th>订单号<small>(주문번호)</small></th>
            <th>物流单号<small>(운송장번호)</small></th>
            <th>货主<small>(업체)</small></th>
            <th>图片<small>(이미지)</small></th>
            <th>商品名<small>(상품명)</small></th>
            <th>需跟客户确认<small>(고객확인사항)</small></th>
            <th>客户回复处理方式<small>(고객 회신 처리방법)</small></th>
            <th>处理状态<small>(처리상태)</small></th>
            <th>处理人<small>(처리인)</small></th>
            <th>确认时间<small>(확인 시각)</small></th>
            <th>作业人<small>(작업자)</small></th>
            <th>确认照片<small>(확인 사진)</small></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in shownRows" :key="r._id" :class="{ 'table-warning': isNew(r) }">
            <td class="confirmed-list__td">
              <b-badge v-if="isNew(r)" variant="danger" class="mr-1">NEW</b-badge>
              <a v-if="orderNo(r)" href="#" title="入库处理(입고처리)에서 열기" @click.prevent="openOrder(r)">{{ orderNo(r).no }}</a>
            </td>
            <td class="confirmed-list__td">{{ trackingNo(r) }}</td>
            <td>{{ r.companyid }}</td>
            <td><img v-if="r.등록이미지" :src="r.등록이미지" class="confirmed-list__img" alt=""></td>
            <td class="confirmed-list__td">
              {{ r.상품명 }}
              <div class="text-muted">{{ r.바코드 }}<span v-if="r.옵션1_중국어 || r.옵션2_중국어"> · {{ [r.옵션1_중국어, r.옵션2_중국어].filter(Boolean).join(' / ') }}</span></div>
            </td>
            <td class="confirmed-list__td">{{ memo(r, 'workMemo') }}</td>
            <td class="confirmed-list__td">{{ memo(r, 'confirmReply') }}</td>
            <!-- 处理状态: 아직 확인출고 전이면 已跟客户确认待入库, 이미 넘어간 건은 처리상태 아래 지금 단계(위치) -->
            <td class="confirmed-list__td">
              {{ handleText(r) }}
              <div v-if="r.processStatus !== '출고확인대기'" class="text-muted">{{ statusText(r.processStatus) }}<span v-if="r.제품위치"> · {{ r.제품위치 }}</span></div>
            </td>
            <!-- 作业人 = 待客户确认 으로 보낸 직원, 处理人 = 处理状态 를 마지막으로 저장한 직원(없으면 确认出库 한 직원). 이름(代号) -->
            <td class="confirmed-list__td">{{ staffLabel(r._handledBy, r._handledByCode) }}</td>
            <td class="confirmed-list__td">{{ fmt(r._confirmedAt) }}</td>
            <td class="confirmed-list__td">{{ staffLabel(r._sentBy, r._sentByCode) }}</td>
            <td>
              <div class="d-flex flex-wrap" style="gap:2px;max-width:120px">
                <a v-for="u in (r._확인사진 || [])" :key="u" :href="u" target="_blank" rel="noopener"><img :src="u" class="confirmed-list__img" alt=""></a>
              </div>
            </td>
          </tr>
          <tr v-if="!shownRows.length">
            <td colspan="12" class="text-center text-muted py-4">没有数据(데이터가 없습니다)</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { processStatusText, confirmStatusText, staffLabel } from '@/utils/coupassStatusText';

// 「새 건」 기준 = 이 직원이 이 탭을 마지막으로 본 시각. 직원(me)별로 브라우저에 둔다.
// 서버에 두면 기기를 바꿔도 이어지지만, 지금은 직원마다 쓰는 PC 가 정해져 있어 브라우저로 충분하다(2026-10-04).
const SEEN_KEY = 'coupassConfirmedSeen:';

export default {
  name: 'ConfirmedImportList',
  props: {
    // 탭이 열려 있는지 — 열리면 「본 것」으로 기록한다
    active: { type: Boolean, default: false }
  },
  data() {
    return {
      rows: [],
      me: '',
      mineOnly: true,
      loading: false,
      seenAt: 0,         // 마지막으로 본 시각(배지 숫자 기준) — 탭을 열면 바로 갱신된다
      highlightSince: 0  // 이번에 탭을 열기 전의 seenAt — 열어 둔 동안 NEW 행 표시 기준
    };
  },
  computed: {
    mineRows() {
      return this.rows.filter(r => r._sentBy && r._sentBy === this.me);
    },
    shownRows() {
      return this.mineOnly ? this.mineRows : this.rows;
    },
    newCount() {
      return this.mineRows.filter(r => this.after(r, this.seenAt)).length;
    }
  },
  watch: {
    newCount(v) {
      this.$emit('new-count', v);
    },
    // 탭을 열면 다시 읽고 「본 것」으로 기록한다(배지 0). 열어 둔 동안 NEW 행 표시는 유지한다
    async active(v) {
      if (!v) return;
      await this.load();
      this.markSeen();
    }
  },
  mounted() {
    this.load();
  },
  // 상단 탭(keep-alive)으로 다른 화면(待客户确认 등)에 갔다 돌아오면 mounted 가 다시 안 돈다 —
  // 그 사이 处理完成 저장된 건이 배지에 안 잡히던 원인. 돌아올 때마다 다시 읽는다(2026-10-04 YZY)
  async activated() {
    if (this.loading) return;
    await this.load();
    if (this.active) this.markSeen();
  },
  methods: {
    confirmStatusText,
    staffLabel,
    memo(r, k) {
      return (r.요청내역 && r.요청내역[k]) || '';
    },
    statusText(s) {
      return processStatusText(s) || s || '';
    },
    // 订单号 = 1688주문번호(입고처리 기본 검색), 없으면 화주주문번호
    orderNo(r) {
      const n1688 = r.주문정보 && r.주문정보['1688주문번호'];
      return n1688 ? { type: 'order1688', no: String(n1688) } : (r.화주주문번호 ? { type: 'order', no: String(r.화주주문번호) } : null);
    },
    trackingNo(r) {
      const 물류 = r.물류정보 || {};
      return (Array.isArray(물류.운송장번호목록) && 물류.운송장번호목록.length) ? 물류.운송장번호목록.join('\n') : (물류.운송장번호 || '');
    },
    // 아직 확인출고 전(待客户确认 그대로)인데 处理完成 으로 저장된 건 = 「已跟客户确认待入库」(2026-10-04 YZY)
    handleText(r) {
      if (r.processStatus === '출고확인대기' && r._confirmStatus === '처리완료') return '已跟客户确认待入库';
      return confirmStatusText(r._confirmStatus);
    },
    // 订单号 누르면 입고처리 탭으로 바꾸고 그 주문을 검색한다(부모 logisaddimportV0 가 처리)
    openOrder(r) {
      const o = this.orderNo(r);
      if (o) this.$emit('open-order', o);
    },
    fmt(v) {
      if (!v) return '';
      const d = new Date(v);
      if (isNaN(d.getTime())) return '';
      return (d.getMonth() + 1) + '/' + d.getDate() + ' ' + d.getHours() + ':' + String(d.getMinutes()).padStart(2, '0');
    },
    after(r, t) {
      return !!r._confirmedAt && new Date(r._confirmedAt).getTime() > t;
    },
    isNew(r) {
      return this.after(r, this.highlightSince);
    },
    readSeen() {
      try {
        return parseInt(localStorage.getItem(SEEN_KEY + this.me)) || 0;
      } catch (e) {
        console.error('확인완료 탭 본 시각 읽기 오류:', e);
        return 0;
      }
    },
    markSeen() {
      if (!this.me) return;
      const latest = this.mineRows.reduce((m, r) => Math.max(m, new Date(r._confirmedAt || 0).getTime() || 0), 0);
      if (!latest) return;
      try {
        localStorage.setItem(SEEN_KEY + this.me, String(Math.max(latest, this.readSeen())));
      } catch (e) {
        console.error('확인완료 탭 본 시각 저장 오류:', e);
      }
      this.seenAt = this.readSeen();
    },
    async load() {
      this.loading = true;
      try {
        const { data } = await axios.post('/api/reqpurchase/import/confirmedlist', {});
        if (data.요청결과 !== '성공') {
          console.error('已跟客户确认 조회 실패:', data);
          if (this.active) alert('搜索失败(검색 실패): ' + ((data.error && data.error.errorMessage) || ''));
          return;
        }
        this.me = data.me || '';
        this.seenAt = this.readSeen();
        this.highlightSince = this.seenAt;
        this.rows = data.rows || [];
      } catch (e) {
        console.error('已跟客户确认 조회 오류:', e);
        if (this.active) alert('搜索错误(검색 오류): ' + e.message);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.confirmed-list {
  max-height: 75vh;
  overflow: auto;

  th,
  td {
    padding: 2px 4px !important;
    font-size: 0.75rem;
    vertical-align: middle;
  }

  &__td {
    white-space: pre-line;
    max-width: 240px;
    overflow-wrap: anywhere;
  }

  &__img {
    width: 36px;
    height: 36px;
    object-fit: cover;
    display: block;
  }
}
</style>
